import Link from 'next/link';
import { useState } from 'react';
import { Button } from 'react-bootstrap';

export enum Circumstance {
  FLIGHT_CANCELLATION = 'Flight cancellation',
  MEDICAL = 'Medical issue',
  OTHER_COMMITMENT = 'Other commitment',
  COVID = 'Covid',
}

/**
 * Exceptional circumstances
 */
export function ExceptionalCircumstanceInfo({
  circumstance,
}: {
  circumstance: Circumstance;
}) {
  switch (circumstance) {
    case Circumstance.COVID:
      return (
        <>
          <p className="mt-4">Sorry to hear this!</p>

          <p>
            If you have not been able to travel due to catching Covid-19,
            unfortunately you will not be covered under our{' '}
            <em>Book With Confidence</em> policy as this only covers changes in
            Government restrictions. In this case please click on the medical
            issue button above instead.
          </p>

          <p>
            If you have not been able to travel due to Government restrictions,
            please <Link href="/help/contact">contact us</Link>, as you may be
            covered under our <em>Book With Confidence</em> policy. Please note
            that this only covers cases where travel is prohibited - if you are
            unable to travel because you have not been vaccinated, or taken the
            appropriate test, this will not be covered.
          </p>
        </>
      );

    case Circumstance.FLIGHT_CANCELLATION:
      return (
        <>
          <p className="mt-4">Sorry to hear your flight was cancelled!</p>

          <p>
            Unfortunately, your local guides are not able to cover you for
            cancellations to your flights, as this is out of their control.
          </p>

          <p>
            In this case, we do recommend that you contact your travel insurance
            provider (as this is the sort of thing that is best handled by
            them). Typically cover for flight cancellations will be under the
            "Cancellation" section of your insurance. To help you claim this,
            please continue to the travel insurance section below.
          </p>
        </>
      );

    case Circumstance.MEDICAL:
      return (
        <>
          <p className="mt-4">
            We're really sorry to hear a medical issue is preventing you from
            traveling!
          </p>

          <p>
            Unfortunately, your local guides are not able to cover you for
            medical issues, as this is out of their control.
          </p>

          <p>
            In this case, we do recommend that you contact your travel insurance
            provider (as this is really the sort of thing that it is designed
            for). To help you claim this, please continue to the travel
            insurance section below.
          </p>
        </>
      );

    case Circumstance.OTHER_COMMITMENT:
      return (
        <>
          <p className="mt-4">
            We're sorry to hear you can no longer make the trip, due to another
            commitment.
          </p>

          <p>
            Unfortunately, your local guides are not able to cover you for this
            sort of thing, as this is out of their control.
          </p>

          <p>
            We do appreciate this may not be the outcome you were hoping for,
            however in these sort of cases we do have to also be fair to the
            local guides who run your trip. However, depending on the
            circumstances you may be able to claim on your travel insurance, so
            it's worth reading on below.
          </p>
        </>
      );

    default:
      return null;
  }
}

export default function ExceptionalCircumstances() {
  const [circumstance, setCircumstance] = useState<Circumstance>();

  return (
    <div className="my-4">
      <h6 className="mb-4">Do you have any exceptional circumstances?</h6>

      {Object.values(Circumstance).map((text) => (
        <Button
          className="me-1 mb-1"
          onClick={() => setCircumstance(text)}
          variant={circumstance === text ? 'primary' : 'outline-primary'}
          key={text}
        >
          {text}
        </Button>
      ))}

      <ExceptionalCircumstanceInfo circumstance={circumstance} />
    </div>
  );
}
